import { EntityLinks } from '@griply/types';
import React, { useMemo } from 'react';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { useTimeBasedStyle } from './use-time-based-style';

export type GoalEventProps = {
  goalId: ID;
  startDate?: string;
  endDate?: string;
  goals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
};

export const GoalEvent: React.FC<GoalEventProps> = ({
  goalId,
  startDate,
  endDate,
  goals,
  lifeAreas,
  links,
  onClick,
  isDeadline,
}) => {
  const goal = useMemo(
    () => goals.find(({ id }) => id === goalId),
    [goals, goalId],
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();

  const { numberOfLines } = useTimeBasedStyle(startDate, endDate);

  const taskCounts = useTaskCounts();

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(
            getGoalCompletion(goal, {
              goals,
              goalProgressCounts: taskCounts?.byGoalProgress,
            }),
          )
        : undefined,
    [goal, goals, taskCounts?.byGoalProgress],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas, links }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas, links],
  );

  if (!goal || !startDate) {
    return null;
  }

  const _onClick = () => onClick?.(goal.id);

  return (
    <>
      <EventCard
        title={goal.name}
        color={color}
        isDeadline={isDeadline}
        completed={!!goal.completedAt}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={
          <Styled.LeadingSlot $color={color} $completed={!!goal.completedAt}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          location={menuPosition}
          onClose={closeMenu}
        />
      )}
    </>
  );
};
